import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpInternalService } from 'src/app/data/service/http.service';
import {
  DashboardCardLayout, DashboardLayoutSettings, DEFAULT_LAYOUT, LAYOUT_VERSION, cloneLayout, ROW_HEIGHTS
} from '../grid/dashboard-layout.model';

/**
 * SD-1209: loads and saves a user's dashboard layout.
 *
 * Stored server-side in dbo.DisplaySettings under SettingsKey = 'dashboard-layout', one row per
 * (organisation, user) -- the same table and controller as the org-level 'invoice-panel'
 * settings, whose actions pass a null user id where these pass the caller's.
 *
 * The localStorage half is a paint cache, not the source of truth. It exists so the grid can
 * render instantly on a revisit instead of flashing empty for a round trip; the server's answer
 * always wins when it arrives. Layout must survive a new device, which is exactly what
 * localStorage cannot do and why this is not localStorage-only.
 */
@Injectable({ providedIn: 'root' })
export class DashboardLayoutService {

  /** Every key starts with this so logout can sweep by prefix -- the ids in it are dynamic. */
  static readonly CACHE_PREFIX = 'synced.dashboardLayout.';

  private static readonly ENDPOINT = 'display-settings/dashboard-layout';

  constructor(private _httpService: HttpInternalService) { }

  /**
   * The caller's layout for an organisation, or null when they have never customised.
   *
   * Never errors: a failure is indistinguishable from "not customised" as far as the grid is
   * concerned, and both mean "show the default". Losing the dashboard because a preferences
   * read failed would be a far worse outcome than losing a customisation for one load.
   */
  load(organisationId: string): Observable<DashboardLayoutSettings | null> {
    if (!organisationId) { return of(null); }

    return this._httpService
      .getRequest<{ settings: DashboardLayoutSettings }>(
        `${DashboardLayoutService.ENDPOINT}?organisationId=${organisationId}`)
      .pipe(
        map(res => this.sanitise(res && res.settings)),
        catchError(() => of(null))
      );
  }

  /** Upserts the caller's layout. The user id comes from the session server-side, not from here. */
  save(organisationId: string, settings: DashboardLayoutSettings): Observable<any> {
    return this._httpService.putRequest(DashboardLayoutService.ENDPOINT, {
      organisationId,
      settings,
    });
  }

  private cacheKey(userId: string, organisationId: string): string {
    return `${DashboardLayoutService.CACHE_PREFIX}${userId}.${organisationId}`;
  }

  /**
   * The cached layout, or null. Storage access throws outright in some private-browsing modes,
   * so every path through here is guarded -- a cache miss must look like "no cache", never like
   * an error the grid has to handle.
   */
  readCache(userId: string, organisationId: string): DashboardLayoutSettings | null {
    if (!userId || !organisationId) { return null; }
    try {
      const raw = localStorage.getItem(this.cacheKey(userId, organisationId));
      return raw ? this.sanitise(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  writeCache(userId: string, organisationId: string, settings: DashboardLayoutSettings): void {
    if (!userId || !organisationId) { return; }
    try {
      localStorage.setItem(this.cacheKey(userId, organisationId), JSON.stringify(settings));
    } catch {
      // Full, blocked or disabled storage. The layout is on the server; the cache is an
      // optimisation, so failing to write one is not worth telling anybody about.
    }
  }

  /**
   * Drops every cached layout for every user and organisation on this browser.
   *
   * Called from both places that clear storage on sign-out. A prefix sweep rather than a
   * literal key because the key carries two ids, so the explicit key lists in auth.service.ts
   * and auth.interceptor.ts cannot name it.
   */
  static clearAllCaches(): void {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf(DashboardLayoutService.CACHE_PREFIX) === 0) {
          doomed.push(key);
        }
      }
      doomed.forEach(k => localStorage.removeItem(k));
    } catch {
      // Nothing to do, and nothing worth failing a sign-out over.
    }
  }

  clearCache(): void {
    DashboardLayoutService.clearAllCaches();
  }

  /**
   * Coerce whatever came back into something the grid can render, or null.
   *
   * Applies to the server's answer and to the cache equally: both are data this build did not
   * write, and a layout from a future version would otherwise render as garbage rather than
   * falling back to a dashboard that works.
   */
  private sanitise(raw: any): DashboardLayoutSettings | null {
    if (!raw || typeof raw !== 'object') { return null; }
    if (raw.version !== LAYOUT_VERSION) { return null; }
    if (!Array.isArray(raw.cards)) { return null; }

    const rowHeight = ROW_HEIGHTS[raw.rowHeight] ? raw.rowHeight : DEFAULT_LAYOUT.rowHeight;
    const cards = raw.cards
      .filter(c => c && typeof c.key === 'string')
      .map(c => {
        const card: DashboardCardLayout = {
          key: c.key,
          x: Number(c.x) || 0,
          y: Number(c.y) || 0,
          w: Number(c.w) || 1,
          h: Number(c.h) || 1,
        };
        // The open tab, so a tabbed card reopens where it was left. Dropping it here sent every
        // reload back to the first tab.
        if (typeof c.tab === 'string' && c.tab) { card.tab = c.tab; }
        return card;
      });

    return { version: LAYOUT_VERSION, rowHeight, cards };
  }

  /** A fresh copy of the default, so callers cannot mutate the shared constant. */
  defaultLayout(): DashboardLayoutSettings {
    return cloneLayout(DEFAULT_LAYOUT);
  }
}
